import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "firebase.js";
import * as Sentry from "@sentry/react";

export const createMatchesFromTeams = async (tournament) => {
  try {
    const divisions = Object.keys(tournament.teams);

    for (const division of divisions) {
      const teams = tournament.teams[division];

      for (let i = 0; i < teams.length; i++) {
        for (let j = i + 1; j < teams.length; j++) {
          const team1 = teams[i];
          const team2 = teams[j];

          const type =
            team1.players.length === 1 && team2.players.length === 1
              ? "singles"
              : "doubles";

          const match = {
            tournamentId: tournament.id,
            division,
            type,
            phase: "group",
            goalsToWin: tournament.goalsToWin || 1, // Default to 1 for qualification matches
            team1: {
              teamNumber: team1.teamNumber,
              players: team1.players.map((p) => ({
                id: p.userId,
                name: p.name || p.userId,
                ranking: p.ranking ?? 0,
              })),
            },
            team2: {
              teamNumber: team2.teamNumber,
              players: team2.players.map((p) => ({
                id: p.userId,
                name: p.name || p.userId,
                ranking: p.ranking ?? 0,
              })),
            },
            result: null,
            createdAt: new Date().toISOString(),
          };

          await addDoc(collection(db, "matches"), match);
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("❌ Failed to create matches:", error);
  }
};

export const createCrosspoolMatches = async (tournament) => {
  try {
    const divisions = Object.keys(tournament.teams);

    if (divisions.length !== 2) {
      throw new Error("Crosspool matches require exactly 2 divisions");
    }

    const [division1, division2] = divisions;
    const teams1 = tournament.teams[division1];
    const teams2 = tournament.teams[division2];

    for (const team1 of teams1) {
      for (const team2 of teams2) {
        const type =
          team1.players.length === 1 && team2.players.length === 1
            ? "singles"
            : "doubles";

        const match = {
          tournamentId: tournament.id,
          division: "crosspool",
          type,
          phase: "crosspool",
          goalsToWin: tournament.goalsToWin || 1, // Default to 1 for crosspool qualification matches
          team1: {
            teamNumber: team1.teamNumber,
            division: division1,
            players: team1.players.map((p) => ({
              id: p.userId,
              name: p.name || p.userId,
              ranking: p.ranking ?? 0,
            })),
          },
          team2: {
            teamNumber: team2.teamNumber,
            division: division2,
            players: team2.players.map((p) => ({
              id: p.userId,
              name: p.name || p.userId,
              ranking: p.ranking ?? 0,
            })),
          },
          result: null,
          createdAt: new Date().toISOString(),
        };

        await addDoc(collection(db, "matches"), match);
      }
    }
  } catch (error) {
    Sentry.captureException(error);
    console.error("❌ Failed to create crosspool matches:", error);
  }
};

export const getMatchesByTournamentId = async (tournamentId) => {
  if (!tournamentId) return [];

  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId)
  ); // ➡️ Linking nextMatchId and auto-advancing BYEs stays the same

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};
export const deleteMatchesByTournamentId = async (tournamentId) => {
  if (!tournamentId) return;

  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId)
  );

  const snapshot = await getDocs(q);
  const deletions = snapshot.docs.map((docSnap) =>
    deleteDoc(doc(db, "matches", docSnap.id))
  );

  await Promise.all(deletions);
};

export const updateMatchResult = async (matchId, result) => {
  const match = await fetchMatchById(matchId);

  // 1. Update current match
  const matchRef = doc(db, "matches", matchId);
  await updateDoc(matchRef, {
    result,
    updatedAt: new Date().toISOString(),
  });

  // 2. Auto-advance if elimination phase
  if (match.phase === "elimination") {
    // ➡️ Advance winner to next match if exists
    if (match.nextMatchId) {
      await advanceWinnerToNextMatch(match, result);
    }

    // ➡️ Advance loser to third place match if semifinal
    if (match.thirdPlaceMatchId) {
      await advanceLoserToThirdPlaceMatch(match, result);
    }
  }
};

export const updateTeamInMatches = async (tournamentId, division, teamNumber, updatedTeam) => {
  try {
    // Get all matches for the tournament
    const matches = await getMatchesByTournamentId(tournamentId);

    // Find matches where this team appears
    const matchesToUpdate = matches.filter((match) => {
      const isTeam1 =
        (match.team1?.teamNumber === teamNumber &&
         (match.division === division || match.team1?.division === division));
      const isTeam2 =
        (match.team2?.teamNumber === teamNumber &&
         (match.division === division || match.team2?.division === division));
      return isTeam1 || isTeam2;
    });

    // Create the updated team data for matches
    const teamDataForMatches = {
      teamNumber: updatedTeam.teamNumber,
      division: division,
      players: updatedTeam.players.map((p) => ({
        id: p.userId,
        name: p.name || p.userId,
        ranking: p.ranking ?? 0,
      })),
    };

    // Update all matching matches
    const updatePromises = matchesToUpdate.map(async (match) => {
      const matchRef = doc(db, "matches", match.id);
      const updates = {};

      // Check if team1 needs updating
      if (match.team1?.teamNumber === teamNumber &&
          (match.division === division || match.team1?.division === division)) {
        updates.team1 = teamDataForMatches;
      }

      // Check if team2 needs updating
      if (match.team2?.teamNumber === teamNumber &&
          (match.division === division || match.team2?.division === division)) {
        updates.team2 = teamDataForMatches;
      }

      if (Object.keys(updates).length > 0) {
        await updateDoc(matchRef, updates);
      }
    });

    await Promise.all(updatePromises);

    return matchesToUpdate.length;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error updating team in matches:", error);
    throw error;
  }
};

const advanceWinnerToNextMatch = async (match, newResult) => {
  if (!match.nextMatchId) return;

  const nextMatch = await fetchMatchById(match.nextMatchId);
  const nextMatchRef = doc(db, "matches", match.nextMatchId);

  const winner = determineWinnerName(match, newResult);
  const loser = determineLoserName(match, newResult);

  const updates = {};

  if (!winner && !loser) {
    // 🛑 Reset teams if no result
    if (
      nextMatch.team1Name === match.team1Name ||
      nextMatch.team1Name === match.team2Name
    ) {
      updates.team1Name = "?";
      updates.team1 = null;
    }
    if (
      nextMatch.team2Name === match.team1Name ||
      nextMatch.team2Name === match.team2Name
    ) {
      updates.team2Name = "?";
      updates.team2 = null;
    }
  } else {
    // Remove loser if present
    if (nextMatch.team1Name === loser) {
      updates.team1Name = "?";
      updates.team1 = null;
    }
    if (nextMatch.team2Name === loser) {
      updates.team2Name = "?";
      updates.team2 = null;
    }

    // Insert winner if not already present
    const winnerAlreadyPresent =
      nextMatch.team1Name === winner || nextMatch.team2Name === winner;

    if (winner && winner !== "?" && !winnerAlreadyPresent) {
      const winnerTeamData =
        winner === match.team1Name ? match.team1 : match.team2;

      // Use designated slot if specified, otherwise find empty slot
      if (match.slotInNextMatch) {
        const teamDataField = match.slotInNextMatch === "team1Name" ? "team1" : "team2";
        updates[match.slotInNextMatch] = winner;
        updates[teamDataField] = winnerTeamData;
      } else {
        // Fallback to original logic for matches without designated slots
        if (
          !nextMatch.team1Name ||
          nextMatch.team1Name === "?" ||
          updates.team1Name === "?"
        ) {
          updates.team1Name = winner;
          updates.team1 = winnerTeamData;
        } else if (
          !nextMatch.team2Name ||
          nextMatch.team2Name === "?" ||
          updates.team2Name === "?"
        ) {
          updates.team2Name = winner;
          updates.team2 = winnerTeamData;
        } else {
          console.warn(
            `⚠️ Cannot insert winner ${winner}, both slots are full in next match.`
          );
        }
      }
    }
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(nextMatchRef, updates);
  }
};

const advanceLoserToThirdPlaceMatch = async (match, result) => {
  if (!match.thirdPlaceMatchId) return;

  const thirdPlaceRef = doc(db, "matches", match.thirdPlaceMatchId);
  const thirdPlaceDoc = await getDoc(thirdPlaceRef);

  if (!thirdPlaceDoc.exists()) {
    console.error("❌ Third place match not found!");
    return;
  }

  const thirdPlaceData = thirdPlaceDoc.data();
  const updates = {};

  const loser = determineLoserName(match, result);
  const winner = determineWinnerName(match, result);

  if (!winner && !loser) {
    // 🛑 Reset teams if no result
    if (
      thirdPlaceData.team1Name === match.team1Name ||
      thirdPlaceData.team1Name === match.team2Name
    ) {
      updates.team1Name = "Perdant Demi-finale 1";
      updates.team1 = null;
    }
    if (
      thirdPlaceData.team2Name === match.team1Name ||
      thirdPlaceData.team2Name === match.team2Name
    ) {
      updates.team2Name = "Perdant Demi-finale 2";
      updates.team2 = null;
    }
  } else {
    // Remove winner if somehow present (safety)
    if (thirdPlaceData.team1Name === winner) {
      updates.team1Name = "Perdant Demi-finale 1";
      updates.team1 = null;
    }
    if (thirdPlaceData.team2Name === winner) {
      updates.team2Name = "Perdant Demi-finale 2";
      updates.team2 = null;
    }

    // Insert loser if not already present
    const loserAlreadyPresent =
      thirdPlaceData.team1Name === loser || thirdPlaceData.team2Name === loser;

    if (!loserAlreadyPresent) {
      const loserTeamData =
        loser === match.team1Name ? match.team1 : match.team2;

      // Check for various placeholder formats
      const isTeam1Placeholder =
        !thirdPlaceData.team1Name ||
        thirdPlaceData.team1Name === "Loser of SF1" ||
        thirdPlaceData.team1Name === "Perdant Demi-finale 1" ||
        updates.team1Name === "Loser of SF1";

      const isTeam2Placeholder =
        !thirdPlaceData.team2Name ||
        thirdPlaceData.team2Name === "Loser of SF2" ||
        thirdPlaceData.team2Name === "Perdant Demi-finale 2" ||
        updates.team2Name === "Loser of SF2";

      if (isTeam1Placeholder) {
        updates.team1Name = loser;
        updates.team1 = loserTeamData;
      } else if (isTeam2Placeholder) {
        updates.team2Name = loser;
        updates.team2 = loserTeamData;
      } else {
        console.warn(
          `⚠️ Cannot insert loser ${loser}, both slots are full in third place match.`
        );
      }
    }
  }

  if (Object.keys(updates).length > 0) {
    await updateDoc(thirdPlaceRef, updates);
  }
};

const fetchMatchById = async (matchId) => {
  const matchRef = doc(db, "matches", matchId);
  const matchSnap = await getDoc(matchRef);

  if (!matchSnap.exists()) {
    throw new Error(`Match with ID ${matchId} not found`);
  }

  return matchSnap.data();
};

const determineWinnerName = (match, result) => {
  const team1Score = result?.team1 ?? 0;
  const team2Score = result?.team2 ?? 0;

  if (team1Score > team2Score) {
    return match.team1Name;
  } else if (team2Score > team1Score) {
    return match.team2Name;
  } else {
    return null; // Tie or unfinished
  }
};

const determineLoserName = (match, result) => {
  if (!result || (result.team1 == null && result.team2 == null)) return null;

  const t1 = result?.team1 ?? 0;
  const t2 = result?.team2 ?? 0;

  if (t1 > t2) {
    return (
      match.team2Name || match.team2?.players?.map((p) => p.name).join(" & ")
    );
  } else if (t2 > t1) {
    return (
      match.team1Name || match.team1?.players?.map((p) => p.name).join(" & ")
    );
  } else {
    // 0-0 or tie: no loser
    return null;
  }
};

export const deleteEliminationMatches = async ({
  tournamentId,
  divisionName,
}) => {
  // Delete elimination matches for the specific division
  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId),
    where("phase", "==", "elimination"),
    where("division", "==", divisionName)
  );

  const snapshot = await getDocs(q);

  // Also delete third place matches (they have division: "third_place")
  const thirdPlaceQuery = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId),
    where("phase", "==", "elimination"),
    where("round", "==", "third_place")
  );

  const thirdPlaceSnapshot = await getDocs(thirdPlaceQuery);

  const deletions = [
    ...snapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "matches", docSnap.id))
    ),
    ...thirdPlaceSnapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, "matches", docSnap.id))
    ),
  ];

  await Promise.all(deletions);
};

export const fetchDivisionStats = async (tournamentId, division) => {
  const q = query(
    collection(db, "matches"),
    where("tournamentId", "==", tournamentId)
  );

  const snapshot = await getDocs(q);

  const matches = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  const stats = calculateStats(matches, division);

  stats.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    return b.difference - a.difference;
  });

  return { stats, matches };
};

export const calculateStats = (matches, division) => {
  const stats = {};

  matches
    .filter((match) => {
      if (division === "merged") {
        return match.result; // take all matches if merged
      }
      if (division === "crosspool") {
        return match.phase === "crosspool" && match.result;
      }
      return match.division === division && match.result;
    })
    .forEach((match) => {
      const { team1, team2, result } = match;
      const team1Name = team1.players.map((p) => p.name).join(" & ");
      const team2Name = team2.players.map((p) => p.name).join(" & ");

      if (!stats[team1Name])
        stats[team1Name] = { team: team1Name, wins: 0, scored: 0, conceded: 0 };
      if (!stats[team2Name])
        stats[team2Name] = { team: team2Name, wins: 0, scored: 0, conceded: 0 };

      const t1Score = result.team1 ?? 0;
      const t2Score = result.team2 ?? 0;

      stats[team1Name].scored += t1Score;
      stats[team1Name].conceded += t2Score;

      stats[team2Name].scored += t2Score;
      stats[team2Name].conceded += t1Score;

      if (t1Score > t2Score) stats[team1Name].wins += 1;
      else if (t2Score > t1Score) stats[team2Name].wins += 1;
    });

  return Object.values(stats).map((stat) => ({
    ...stat,
    difference: stat.scored - stat.conceded,
  }));
};

export const calculateCrosspoolStats = (matches, targetDivision) => {
  const stats = {};

  matches
    .filter((match) =>
      match.phase === "crosspool" &&
      match.result &&
      (match.team1.division === targetDivision || match.team2.division === targetDivision)
    )
    .forEach((match) => {
      const { team1, team2, result } = match;

      // Only calculate stats for teams from the target division
      if (team1.division === targetDivision) {
        const teamName = team1.players.map((p) => p.name).join(" & ");
        if (!stats[teamName])
          stats[teamName] = { team: teamName, wins: 0, scored: 0, conceded: 0 };

        const t1Score = result.team1 ?? 0;
        const t2Score = result.team2 ?? 0;

        stats[teamName].scored += t1Score;
        stats[teamName].conceded += t2Score;

        if (t1Score > t2Score) stats[teamName].wins += 1;
      }

      if (team2.division === targetDivision) {
        const teamName = team2.players.map((p) => p.name).join(" & ");
        if (!stats[teamName])
          stats[teamName] = { team: teamName, wins: 0, scored: 0, conceded: 0 };

        const t1Score = result.team1 ?? 0;
        const t2Score = result.team2 ?? 0;

        stats[teamName].scored += t2Score;
        stats[teamName].conceded += t1Score;

        if (t2Score > t1Score) stats[teamName].wins += 1;
      }
    });

  return Object.values(stats).map((stat) => ({
    ...stat,
    difference: stat.scored - stat.conceded,
  }));
};

// 🧹 Cleaned main function
export const createEliminationMatches = async ({
  tournamentId,
  divisionName,
  bracketSize = 8,
}) => {
  if (!tournamentId || !divisionName) {
    throw new Error(
      "Invalid tournament or division to create elimination matches."
    );
  }

  if (divisionName === "merged") {
    await createMergedElimination(tournamentId, bracketSize);
  } else if (divisionName === "crosspool") {
    await createCrosspoolElimination(tournamentId, bracketSize);
  } else {
    await createSimpleElimination(tournamentId, divisionName, bracketSize);
  }
};

export const createCrosspoolElimination = async (tournamentId, bracketSize = 8) => {
  try {
    const divisions = ["division1", "division2"];

    // Get all matches for the tournament
    const allMatches = await getMatchesByTournamentId(tournamentId);

    let poolWinners = [];

    for (const division of divisions) {
      // Use crosspool stats to get team rankings within each division
      const stats = calculateCrosspoolStats(allMatches, division);

      if (stats.length === 0) {
        throw new Error(`No teams available for ${division}`);
      }

      // Sort teams by performance
      stats.sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.difference - a.difference;
      });

      const teamCount = stats.length;

      // If only 1 team in pool, they automatically advance to final
      if (teamCount === 1) {
        poolWinners.push({
          division,
          winner: stats[0].team,
          teamData: getTeamDataByName(allMatches, stats[0].team)
        });
        continue;
      }

      // Use the minimum bracket size that fits all teams (no unnecessary BYEs)
      const actualBracketSize = Math.min(bracketSize, getOptimalBracketSize(teamCount));

      const realTeams = stats.map((s) => s.team).slice(0, actualBracketSize);

      // Only add BYEs if we have fewer teams than bracket spots
      while (realTeams.length < actualBracketSize && isPowerOfTwo(actualBracketSize)) {
        realTeams.push("BYE");
      }

      const bracketTree = buildBracketTreeFromTeamsCrosspool(realTeams, division);
      await saveBracket(allMatches, tournamentId, division, bracketTree);
    }

    // Create the final match between pool winners
    await createFinalMatchCrosspool(tournamentId, poolWinners);
  } catch (error) {
    Sentry.captureException(error);
    console.error("❌ Failed to create crosspool elimination:", error);
    throw error;
  }
};

// Helper function to get team data by name from matches
const getTeamDataByName = (matches, teamName) => {
  for (const match of matches) {
    if (match.team1?.players) {
      const name = match.team1.players.map(p => p.name).join(" & ");
      if (name === teamName) return match.team1;
    }
    if (match.team2?.players) {
      const name = match.team2.players.map(p => p.name).join(" & ");
      if (name === teamName) return match.team2;
    }
  }
  return null;
};

// Helper function to get the optimal bracket size for a given number of teams
const getOptimalBracketSize = (teamCount) => {
  const sizes = [2, 4, 8, 16, 32, 64];
  return sizes.find(size => size >= teamCount) || teamCount;
};

// Helper function to check if a number is a power of 2
const isPowerOfTwo = (n) => {
  return n > 0 && (n & (n - 1)) === 0;
};

// Modified bracket builder for crosspool with tournament-wide round naming
const buildBracketTreeFromTeamsCrosspool = (teams, division) => {
  const matches = [];
  let matchId = 1;

  const n = teams.length;

  // Special case: if only 2 teams, the match is a semifinal from tournament perspective
  if (n === 2) {
    const semifinalMatch = {
      id: matchId++,
      team1Name: teams[0],
      team2Name: teams[1],
      round: "semifinal",
      nextMatchId: null,
      slotInNextMatch: null,
    };
    return [semifinalMatch];
  }

  // Build the bracket, but shift all round names down by one level
  // since pool finals are tournament semifinals
  const firstRound = getFirstRoundPairs(n).map(([seed1, seed2]) => ({
    id: matchId++,
    team1Name: teams[seed1 - 1] || "BYE",
    team2Name: teams[seed2 - 1] || "BYE",
    round: getCrosspoolRoundName(n, n),
    nextMatchId: null,
    slotInNextMatch: null,
  }));

  matches.push(firstRound);

  let previousRound = firstRound;
  let currentLevelTeams = n;

  while (previousRound.length > 1) {
    const nextRound = [];
    const nextRoundTeams = Math.ceil(previousRound.length / 2);
    currentLevelTeams = nextRoundTeams;

    for (let i = 0; i < previousRound.length; i += 2) {
      const isPoolFinal = nextRoundTeams === 1;
      const roundName = getCrosspoolRoundName(previousRound.length, n);

      const nextMatch = {
        id: matchId++,
        team1Name: "?",
        team2Name: "?",
        round: roundName,
        nextMatchId: null,
        slotInNextMatch: null,
      };

      previousRound[i].nextMatchId = nextMatch.id;
      previousRound[i].slotInNextMatch = "team1Name";

      if (previousRound[i + 1]) {
        previousRound[i + 1].nextMatchId = nextMatch.id;
        previousRound[i + 1].slotInNextMatch = "team2Name";
      }

      nextRound.push(nextMatch);
    }

    matches.push(nextRound);
    previousRound = nextRound;
  }

  return matches.flat();
};

// Helper function to get correct round names for crosspool tournaments
const getCrosspoolRoundName = (currentRoundTeams, initialPoolSize) => {
  // In crosspool, pool finals are tournament semifinals
  // So we need to shift the naming perspective

  if (currentRoundTeams === 2) {
    // Pool final = tournament semifinal
    return "semifinal";
  }

  // For other rounds, calculate what they would be in tournament context
  // currentRoundTeams represents how many teams are competing in this round
  // We need to map this to the tournament-wide perspective

  // For 8-team pools: 8→4→2→1
  if (initialPoolSize === 8) {
    switch (currentRoundTeams) {
      case 8:
        return "round_of_8";    // 8→4, first elimination round (8e de finale)
      case 4:
        return "quarterfinal";  // 4→2, second elimination round (quarterfinals)
      case 2:
        return "semifinal";     // 2→1, pool final (tournament semifinal)
      default:
        return "first_round";
    }
  }

  // For other pool sizes, use more conservative mapping
  switch (currentRoundTeams) {
    case 2:
      return "quarterfinal";
    case 4:
      return "quarterfinal";
    case 8:
      return "quarterfinal";  // Changed from round_of_16 to quarterfinal
    case 16:
      return "round_of_16";   // Changed from round_of_32 to round_of_16
    default:
      return "first_round";
  }
};

const createFinalMatch = async (tournamentId) => {
  try {
    // Find the semifinal matches of each division (pool finals are semifinals in crosspool)
    const div1SemiFinalQuery = query(
      collection(db, "matches"),
      where("tournamentId", "==", tournamentId),
      where("division", "==", "division1"),
      where("phase", "==", "elimination"),
      where("round", "==", "semifinal")
    );

    const div2SemiFinalQuery = query(
      collection(db, "matches"),
      where("tournamentId", "==", tournamentId),
      where("division", "==", "division2"),
      where("phase", "==", "elimination"),
      where("round", "==", "semifinal")
    );

    const [div1Snapshot, div2Snapshot] = await Promise.all([
      getDocs(div1SemiFinalQuery),
      getDocs(div2SemiFinalQuery)
    ]);

    if (div1Snapshot.empty || div2Snapshot.empty) {
      throw new Error("Both pool semifinals must exist before creating inter-pool final");
    }

    const finalMatch = {
      tournamentId,
      division: "final",
      type: "doubles",
      phase: "elimination",
      round: "final",
      goalsToWin: 1, // Default to 1 for final matches
      team1Name: "Winner of Pool 1",
      team2Name: "Winner of Pool 2",
      team1: null,
      team2: null,
      nextMatchId: null,
      slotInNextMatch: null,
      thirdPlaceMatchId: null,
      result: null,
      createdAt: new Date().toISOString(),
    };

    const docRef = await addDoc(collection(db, "matches"), finalMatch);

    // Link pool semifinals to the inter-pool final
    const finalMatchId = docRef.id;
    const batch = writeBatch(db);

    const div1SemiFinal = div1Snapshot.docs[0];
    const div2SemiFinal = div2Snapshot.docs[0];

    batch.update(div1SemiFinal.ref, { nextMatchId: finalMatchId, slotInNextMatch: "team1Name" });
    batch.update(div2SemiFinal.ref, { nextMatchId: finalMatchId, slotInNextMatch: "team2Name" });

    await batch.commit();

    return finalMatchId;
  } catch (error) {
    Sentry.captureException(error);
    console.error("❌ Failed to create final match:", error);
    throw error;
  }
};

const createFinalMatchCrosspool = async (tournamentId, poolWinners = []) => {
  try {
    // Determine team names and data for the final
    let team1Name = "Gagnant Groupe 1";
    let team2Name = "Gagnant Groupe 2";
    let team1Data = null;
    let team2Data = null;

    // Check if any pools have automatic winners (only 1 team)
    const pool1Winner = poolWinners.find(w => w.division === "division1");
    const pool2Winner = poolWinners.find(w => w.division === "division2");

    if (pool1Winner) {
      team1Name = pool1Winner.winner;
      team1Data = pool1Winner.teamData;
    }
    if (pool2Winner) {
      team2Name = pool2Winner.winner;
      team2Data = pool2Winner.teamData;
    }

    // Create third place match first
    const thirdPlaceMatch = {
      tournamentId,
      division: "third_place",
      type: "doubles",
      phase: "elimination",
      round: "third_place",
      goalsToWin: 1, // Default to 1 for third place matches
      team1Name: "Perdant Demi-finale 1",
      team2Name: "Perdant Demi-finale 2",
      team1: null,
      team2: null,
      nextMatchId: null,
      slotInNextMatch: null,
      thirdPlaceMatchId: null,
      result: null,
      createdAt: new Date().toISOString(),
    };

    const thirdPlaceDocRef = await addDoc(collection(db, "matches"), thirdPlaceMatch);
    const thirdPlaceMatchId = thirdPlaceDocRef.id;

    // Create final match
    const finalMatch = {
      tournamentId,
      division: "final",
      type: "doubles",
      phase: "elimination",
      round: "final",
      goalsToWin: 1, // Default to 1 for final matches
      team1Name,
      team2Name,
      team1: team1Data,
      team2: team2Data,
      nextMatchId: null,
      slotInNextMatch: null,
      thirdPlaceMatchId: null,
      result: null,
      createdAt: new Date().toISOString(),
    };

    const finalDocRef = await addDoc(collection(db, "matches"), finalMatch);
    const finalMatchId = finalDocRef.id;

    // Link pool semifinals to both final and third place matches
    const batch = writeBatch(db);

    if (!pool1Winner) {
      // Link Pool 1 semifinal
      const div1SemiFinalQuery = query(
        collection(db, "matches"),
        where("tournamentId", "==", tournamentId),
        where("division", "==", "division1"),
        where("phase", "==", "elimination"),
        where("round", "==", "semifinal")
      );
      const div1Snapshot = await getDocs(div1SemiFinalQuery);
      if (!div1Snapshot.empty) {
        const div1SemiFinal = div1Snapshot.docs[0];
        batch.update(div1SemiFinal.ref, {
          nextMatchId: finalMatchId,
          slotInNextMatch: "team1Name",
          thirdPlaceMatchId: thirdPlaceMatchId
        });
      }
    }

    if (!pool2Winner) {
      // Link Pool 2 semifinal
      const div2SemiFinalQuery = query(
        collection(db, "matches"),
        where("tournamentId", "==", tournamentId),
        where("division", "==", "division2"),
        where("phase", "==", "elimination"),
        where("round", "==", "semifinal")
      );
      const div2Snapshot = await getDocs(div2SemiFinalQuery);
      if (!div2Snapshot.empty) {
        const div2SemiFinal = div2Snapshot.docs[0];
        batch.update(div2SemiFinal.ref, {
          nextMatchId: finalMatchId,
          slotInNextMatch: "team2Name",
          thirdPlaceMatchId: thirdPlaceMatchId
        });
      }
    }

    // Check if there are operations to commit
    let hasOperations = false;
    if (!pool1Winner || !pool2Winner) {
      hasOperations = true;
    }

    if (hasOperations) {
      await batch.commit();
    }

    return finalMatchId;
  } catch (error) {
    Sentry.captureException(error);
    console.error("❌ Failed to create crosspool final match:", error);
    throw error;
  }
};

const createMergedElimination = async (tournamentId, bracketSize) => {
  const { stats: division1Stats, matches: division1Matches } =
    await fetchDivisionStats(tournamentId, "division1");
  const { stats: division2Stats, matches: division2Matches } =
    await fetchDivisionStats(tournamentId, "division2");

  const allMatches = [...division1Matches, ...division2Matches];

  const halfSize = bracketSize / 2;
  let topDivision1 = division1Stats.map((s) => s.team).slice(0, halfSize);
  let topDivision2 = division2Stats.map((s) => s.team).slice(0, halfSize);

  while (topDivision1.length < halfSize) topDivision1.push("BYE");
  while (topDivision2.length < halfSize) topDivision2.push("BYE");

  const firstRoundMatches = crossPoolsAndHandleByes(topDivision1, topDivision2);
  const bracketTree = buildBracketTreeFromFirstRound(firstRoundMatches);

  await saveBracket(allMatches, tournamentId, "merged", bracketTree);
};

const createSimpleElimination = async (
  tournamentId,
  divisionName,
  bracketSize
) => {
  const { stats, matches } = await fetchDivisionStats(
    tournamentId,
    divisionName
  );

  if (stats.length === 0) {
    throw new Error(`Aucune équipe disponible pour ${divisionName}`);
  }

  const realTeams = stats.map((s) => s.team).slice(0, bracketSize);

  while (realTeams.length < bracketSize) {
    realTeams.push("BYE");
  }

  const bracketTree = buildBracketTreeFromTeams(realTeams);

  await saveBracket(matches, tournamentId, divisionName, bracketTree);
};

const crossPoolsAndHandleByes = (poolA, poolB) => {
  const matches = [];
  const n = poolA.length;

  for (let i = 0; i < n; i++) {
    const teamA = poolA[i];
    const teamB = poolB[n - i - 1];
    matches.push([teamA, teamB]);
  }

  return matches;
};

// 🛠 Build bracket tree directly from first-round matches
const buildBracketTreeFromFirstRound = (firstRoundMatches) => {
  const matches = [];
  let matchId = 1;

  const firstRound = firstRoundMatches.map(([team1, team2]) => ({
    id: matchId++,
    team1Name: team1,
    team2Name: team2,
    round: getRoundName(firstRoundMatches.length * 2),
    nextMatchId: null,
    slotInNextMatch: null,
  }));

  matches.push(firstRound);

  let previousRound = firstRound;
  let semifinals = [];

  while (previousRound.length > 1) {
    const nextRound = [];
    for (let i = 0; i < previousRound.length; i += 2) {
      const nextMatch = {
        id: matchId++,
        team1Name: "?",
        team2Name: "?",
        round: getRoundName(previousRound.length),
        nextMatchId: null,
        slotInNextMatch: null,
      };

      previousRound[i].nextMatchId = nextMatch.id;
      previousRound[i].slotInNextMatch = "team1Name";

      previousRound[i + 1].nextMatchId = nextMatch.id;
      previousRound[i + 1].slotInNextMatch = "team2Name";

      nextRound.push(nextMatch);
    }

    // Save semifinals before moving to the final
    if (nextRound.length === 1) {
      semifinals = previousRound;
    }

    matches.push(nextRound);
    previousRound = nextRound;
  }

  // ➡️ Add third place match
  if (semifinals.length === 2) {
    const thirdPlaceMatch = {
      id: matchId++,
      team1Name: "Perdant Demi-finale 1",
      team2Name: "Perdant Demi-finale 2",
      round: "third_place",
      nextMatchId: null,
      slotInNextMatch: null,
    };

    matches.push([thirdPlaceMatch]);

    // ✅ Link semifinals to third place
    semifinals.forEach((semi) => {
      semi.thirdPlaceMatchId = thirdPlaceMatch.id;
    });
  }

  return matches.flat();
};

// 🛠 Build bracket tree from simple seeding
const buildBracketTreeFromTeams = (teams) => {
  const matches = [];
  let matchId = 1;

  const n = teams.length;
  const firstRound = getFirstRoundPairs(n).map(([seed1, seed2]) => ({
    id: matchId++,
    team1Name: teams[seed1 - 1] || "BYE",
    team2Name: teams[seed2 - 1] || "BYE",
    round: getRoundName(n),
    nextMatchId: null,
    slotInNextMatch: null,
  }));

  matches.push(firstRound);

  let previousRound = firstRound;
  let semifinals = [];

  while (previousRound.length > 1) {
    const nextRound = [];
    for (let i = 0; i < previousRound.length; i += 2) {
      const nextMatch = {
        id: matchId++,
        team1Name: "?",
        team2Name: "?",
        round: getRoundName(previousRound.length),
        nextMatchId: null,
        slotInNextMatch: null,
      };

      previousRound[i].nextMatchId = nextMatch.id;
      previousRound[i].slotInNextMatch = "team1Name";

      previousRound[i + 1].nextMatchId = nextMatch.id;
      previousRound[i + 1].slotInNextMatch = "team2Name";

      nextRound.push(nextMatch);
    }

    // Save semifinals (before the final round)
    if (nextRound.length === 1) {
      semifinals = previousRound;
    }

    matches.push(nextRound);
    previousRound = nextRound;
  }

  // ➡️ Add third place match
  if (semifinals.length === 2) {
    const thirdPlaceMatch = {
      id: matchId++,
      team1Name: "Perdant Demi-finale 1",
      team2Name: "Perdant Demi-finale 2",
      round: "third_place",
      nextMatchId: null,
      slotInNextMatch: null,
    };

    matches.push([thirdPlaceMatch]);

    // ✅ Link semifinals losers to third place match
    semifinals.forEach((semi) => {
      semi.thirdPlaceMatchId = thirdPlaceMatch.id;
    });
  }

  return matches.flat();
};

// 📋 Get first round seeding order
const getFirstRoundPairs = (n) => {
  if (n === 2) return [[1, 2]];
  if (n === 4)
    return [
      [1, 4],
      [2, 3],
    ];
  if (n === 8)
    return [
      [1, 8],
      [4, 5],
      [2, 7],
      [3, 6],
    ];
  if (n === 16)
    return [
      [1, 16],
      [8, 9],
      [5, 12],
      [4, 13],
      [6, 11],
      [3, 14],
      [7, 10],
      [2, 15],
    ];
  if (n === 32)
    return [
      [1, 32],
      [16, 17],
      [8, 25],
      [9, 24],
      [5, 28],
      [12, 21],
      [4, 29],
      [13, 20],
      [6, 27],
      [11, 22],
      [3, 30],
      [14, 19],
      [7, 26],
      [10, 23],
      [2, 31],
      [15, 18],
    ];
  // fallback basic pairing
  const pairs = [];
  for (let i = 1; i <= n; i += 2) {
    pairs.push([i, i + 1]);
  }
  return pairs;
};

const saveBracket = async (
  matches,
  tournamentId,
  divisionName,
  bracketTree
) => {
  const matchesCollection = collection(db, "matches");
  const localIdToFirestoreId = {};

  // 🔵 (You still need to prepare teamDataByName from matches)
  const teamDataByName = {};

  matches.forEach((match) => {
    if (match.team1?.players) {
      const name = match.team1.players.map((p) => p.name).join(" & ");
      teamDataByName[name] = {
        teamNumber: match.team1.teamNumber ?? null,
        players: match.team1.players ?? [],
      };
    }
    if (match.team2?.players) {
      const name = match.team2.players.map((p) => p.name).join(" & ");
      teamDataByName[name] = {
        teamNumber: match.team2.teamNumber ?? null,
        players: match.team2.players ?? [],
      };
    }
  });

  // 🔥 Now saving matches
  for (const match of bracketTree) {
    const isBye = match.team1Name === "BYE" || match.team2Name === "BYE";
    const initialResult = isBye
      ? {
          team1: match.team1Name !== "BYE" ? 1 : 0,
          team2: match.team2Name !== "BYE" ? 1 : 0,
        }
      : null;

    // 🧠 If match.team1 is already set, reuse it. Otherwise fallback to teamDataByName
    const team1Data = match.team1 ?? teamDataByName[match.team1Name] ?? null;
    const team2Data = match.team2 ?? teamDataByName[match.team2Name] ?? null;

    const docRef = await addDoc(matchesCollection, {
      tournamentId,
      division: divisionName,
      type: "doubles",
      phase: "elimination",
      round: match.round,
      goalsToWin: 1, // Default to 1 for elimination matches
      team1Name: match.team1Name,
      team2Name: match.team2Name,
      team1: team1Data,
      team2: team2Data,
      nextMatchId: null,
      slotInNextMatch: match.slotInNextMatch,
      thirdPlaceMatchId: null,
      result: initialResult,
      createdAt: new Date().toISOString(),
    });

    localIdToFirestoreId[match.id] = docRef.id;
    match.firestoreId = docRef.id;
  }

  // Step 2: Link matches
  for (const match of bracketTree) {
    const updates = {};

    if (match.nextMatchId) {
      updates.nextMatchId = localIdToFirestoreId[match.nextMatchId] || null;
    }

    if (match.thirdPlaceMatchId) {
      updates.thirdPlaceMatchId =
        localIdToFirestoreId[match.thirdPlaceMatchId] || null;
    }

    if (Object.keys(updates).length > 0) {
      const matchRef = doc(db, "matches", match.firestoreId);
      await updateDoc(matchRef, updates);
    }
  }

  // Step 3: Auto-advance BYE
  for (const match of bracketTree) {
    if (!match.nextMatchId) continue;

    const isTeam1Bye = match.team1Name === "BYE";
    const isTeam2Bye = match.team2Name === "BYE";

    if (isTeam1Bye && isTeam2Bye) continue;

    if (isTeam1Bye || isTeam2Bye) {
      const winnerTeamName = isTeam1Bye ? match.team2Name : match.team1Name;
      const winnerTeamData = isTeam1Bye
        ? teamDataByName[match.team2Name]
        : teamDataByName[match.team1Name];
      const teamDataField =
        match.slotInNextMatch === "team1Name" ? "team1" : "team2";

      const nextMatchRef = doc(
        db,
        "matches",
        localIdToFirestoreId[match.nextMatchId]
      );
      await updateDoc(nextMatchRef, {
        [match.slotInNextMatch]: winnerTeamName,
        [teamDataField]: winnerTeamData,
      });
    }
  }
};

// 🛠 Utility
const getRoundName = (size) => {
  switch (size) {
    case 2:
      return "final";
    case 4:
      return "semifinal";
    case 8:
      return "quarterfinal";
    case 16:
      return "round_of_16";
    case 32:
      return "round_of_32";
    default:
      return "first_round";
  }
};
